import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/shared/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { getResetPasswordUrl } from '@/utils/url';
import { useI18n } from '@/contexts/I18nContext';
import logo from '@/assets/images/logo/logo.svg';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export const ForgotPasswordForm = ({ onBack }: ForgotPasswordFormProps) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value.trim());
  };

  const onSubmit = async ({ email }: { email: string }) => {
    const trimmedEmail = email.trim();
    if (trimmedEmail !== email) {
      setEmail(trimmedEmail);
    }
    setLoading(true);
    try {
      const { data: emailExists, error: checkError } = await supabase.rpc('check_email_exists', { email_to_check: trimmedEmail });
      if (checkError) {
        toast({
          title: t('reset_password.errors.email_not_found'),
          description: t('reset_password.errors.email_check_failed'),
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
      if (!emailExists) {
        toast({
          title: t('reset_password.errors.email_not_found'),
          description: t('reset_password.errors.email_not_registered'),
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
      const resetUrl = getResetPasswordUrl();
      const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: resetUrl,
      });

      if (error) {
        console.error('Password reset error:', error);
        toast({
          title: t('reset_password.errors.email_not_found'),
          description: error.message,
          variant: "destructive"
        });
      } else {
        navigate(`/reset-password-sent?email=${encodeURIComponent(trimmedEmail)}`);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: t('reset_password.errors.email_not_found'),
        description: t('reset_password.errors.unexpected_error'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit({ email }); }} className="space-y-6">
      <div className="flex items-center mb-6">
        <button
          type="button"
          onClick={onBack}
          className="text-white/80 hover:text-white mr-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-semibold text-white font-sans">{t('reset_password.title')}</h2>
      </div>

      <div className="space-y-4">
        <p className="text-white/80 text-sm">
          {t('reset_password.description')}
        </p>
        
        <Input
          type="email"
          placeholder={t('reset_password.email_placeholder')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailTouched) {
              setEmailError(validateEmail(e.target.value) ? '' : t('auth.step_one.validation.email_invalid'));
            }
          }}
          onBlur={() => {
            setEmailTouched(true);
            const trimmed = email.trim();
            if (trimmed !== email) {
              setEmail(trimmed);
            }
            setEmailError(validateEmail(trimmed) ? '' : t('auth.step_one.validation.email_invalid'));
          }}
          className="bg-white/20 backdrop-blur-sm border-2 border-white text-white placeholder:text-white focus:ring-white/30 focus:border-white rounded-lg px-6 py-4 h-14"
          required
        />
        {emailTouched && emailError && (
          <p className="mt-1 text-sm text-white">{emailError}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-sossiety-red hover:bg-sossiety-red-hover text-white font-medium py-4 h-14 rounded-full transition-colors duration-200 disabled:opacity-50"
      >
        {loading ? t('reset_password.sending') : t('reset_password.send_email')}
      </Button>
    </form>
  );
};
